/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { FaXmark } from "react-icons/fa6";
import { Gift, Percent, Tag } from "lucide-react";
import toast from "react-hot-toast";
import { applySpendOfferApi } from "@/api/take-order/take-order";

function SpendOfferModal({ isOpen, onClose, isTabletView, order, onSuccess }) {
    const [selectedOfferId, setSelectedOfferId] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        // Pre-select the currently applied offer if it exists
        if (order?.applied_spend_offer) {
            setSelectedOfferId(order.applied_spend_offer.id);
        } else {
            setSelectedOfferId(null);
        }
    }, [isOpen, order]);

    const handleApply = async () => {
        if (!order?.id) {
            toast.error("Order ID not found.");
            return;
        }

        setLoading(true);
        try {
            const res = await applySpendOfferApi(order.id, selectedOfferId);
            if (res?.status === "success") {
                toast.success(res?.message || "Spend offer updated successfully.");
                if (onSuccess) {
                    onSuccess(res?.data?.order || res?.data);
                }
                onClose();
            } else {
                toast.error(res?.message || "Failed to update spend offer.");
            }
        } catch (error) {
            console.error("Error applying spend offer:", error);
            toast.error("Something went wrong while applying the offer.");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const offers = order?.eligible_spend_offers || [];
    const hasAppliedOffer = !!order?.applied_spend_offer;

    return (
        <div className="fixed top-0 left-0 w-screen h-screen bg-black/70 flex items-center justify-center z-[100]">
            <div className={`${isTabletView ? "w-[400px] h-[500px] p-4" : "w-[500px] h-[600px] p-6"} bg-color2 rounded-md relative border-2 border-color3 text-white flex flex-col`}>
                
                {/* Header */}
                <button onClick={onClose} className="absolute top-2.5 right-2.5 text-color8 hover:opacity-80 cursor-pointer transition-opacity">
                    <FaXmark size={22} />
                </button>
                <h2 className={`${isTabletView ? "text-lg" : "text-2xl"} text-center text-color6 font-semibold mb-4 flex items-center justify-center gap-2`}>
                    <Gift className="w-6 h-6 stroke-color6" /> Spend Offers
                </h2>

                {/* Subtitle / Applied Offer Status */}
                {hasAppliedOffer && (
                    <div className="mb-4 p-2.5 bg-emerald-950/40 border border-emerald-500/30 rounded-md flex items-center justify-between text-xs">
                        <div className="flex flex-col">
                            <span className="font-semibold text-emerald-400">Currently Applied Offer:</span>
                            <span className="text-gray-300 font-medium">{order.applied_spend_offer.name}</span>
                        </div>
                        <span className="text-[10px] bg-emerald-500 text-black px-1.5 py-0.5 rounded font-bold uppercase">Active</span>
                    </div>
                )}

                {/* Offers List Container */}
                <div className="flex-1 overflow-y-auto pr-1 mb-4 space-y-3 custom-scrollbar">
                    {offers.length === 0 ? (
                        <div className="text-center py-6 text-gray-400 text-sm">
                            No spend offers currently eligible for this order.
                        </div>
                    ) : (
                        <>
                            {/* Standard None Option to remove offers */}
                            <div 
                                onClick={() => setSelectedOfferId(null)}
                                className={`p-3 rounded-md border-2 cursor-pointer transition-all flex items-center justify-between ${
                                    selectedOfferId === null 
                                        ? "bg-color3 border-color6 shadow-md" 
                                        : "bg-color3/50 border-transparent hover:bg-color3/80"
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selectedOfferId === null ? "border-color6" : "border-gray-500"}`}>
                                        {selectedOfferId === null && <div className="w-2 h-2 rounded-full bg-color6" />}
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-sm font-semibold">None (No Offer)</span>
                                        <span className="text-xs text-gray-400">Do not apply any offer to this order</span>
                                    </div>
                                </div>
                            </div>

                            {/* Offers mapping */}
                            {offers.map((offer) => {
                                const isSelected = selectedOfferId === offer.id;
                                const isCurrent = order?.applied_spend_offer?.id === offer.id;
                                
                                return (
                                    <div 
                                        key={offer.id}
                                        onClick={() => setSelectedOfferId(offer.id)}
                                        className={`p-3.5 rounded-md border-2 cursor-pointer transition-all flex flex-col gap-2 relative ${
                                            isSelected 
                                                ? "bg-color3 border-color6 shadow-md" 
                                                : "bg-color3/50 border-transparent hover:bg-color3/80"
                                        }`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-3">
                                                {/* Checkbox circle */}
                                                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center mt-1 shrink-0 ${isSelected ? "border-color6" : "border-gray-500"}`}>
                                                    {isSelected && <div className="w-2 h-2 rounded-full bg-color6" />}
                                                </div>
                                                
                                                {/* Offer Info */}
                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="text-sm font-semibold text-white">{offer.name}</span>
                                                        {isCurrent && (
                                                            <span className="text-[9px] bg-emerald-600/30 text-emerald-400 border border-emerald-500/50 px-1 py-0.2 rounded font-medium">
                                                                Current
                                                            </span>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Threshold Badge */}
                                                    <span className="text-xs text-color6 font-medium mt-0.5">
                                                        Min Spend: £{offer.spend_threshold}
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            {/* Type Icon */}
                                            <div className="p-1.5 rounded bg-color3 shrink-0">
                                                {offer.type === "spend_get_discount" ? (
                                                    <Percent className="w-4 h-4 text-color6" />
                                                ) : (
                                                    <Gift className="w-4 h-4 text-color6" />
                                                )}
                                            </div>
                                        </div>

                                        {/* Description of benefits */}
                                        <div className="pl-7 text-xs text-gray-300">
                                            {offer.type === "spend_get_discount" ? (
                                                <p>
                                                    Get <span className="font-bold text-white">{offer.discount_value}%</span> discount on order subtotal.
                                                </p>
                                            ) : (
                                                <div className="flex flex-col gap-1">
                                                    <p>Get the following item(s) for free:</p>
                                                    <div className="flex flex-wrap gap-1.5 mt-1">
                                                        {offer.get_items?.map((item, idx) => (
                                                            <span key={idx} className="bg-color1/60 border border-color3 px-2 py-0.5 rounded text-[10px] text-gray-200">
                                                                🎁 {item.quantity}x {item.item_type || "item"}
                                                            </span>
                                                        )) || <span className="text-gray-400">Free item reward</span>}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>

                {/* Footer Buttons */}
                <div className={`flex ${isTabletView ? "flex-col gap-2" : "gap-4"} mt-2`}>
                    <button 
                        onClick={handleApply}
                        disabled={loading}
                        className={`w-full ${isTabletView ? "p-2 text-sm" : "p-3 text-base"} bg-color7 text-black hover:opacity-90 font-bold rounded-md transition-opacity disabled:opacity-50`}
                    >
                        {loading ? "Applying..." : "Apply Offer"}
                    </button>
                    <button 
                        onClick={onClose}
                        disabled={loading}
                        className={`w-full ${isTabletView ? "p-2 text-sm" : "p-3 text-base"} bg-color3 text-white hover:bg-color3/80 font-bold rounded-md transition-colors disabled:opacity-50`}
                    >
                        Cancel
                    </button>
                </div>

            </div>
        </div>
    );
}

export default SpendOfferModal;
